import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { School, ArrowRight } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Nav from "@/components/Nav";
import GatewayDemoSwitcher from "@/components/demo/GatewayDemoSwitcher";
import { getToken, setToken } from "@/lib/api";
import { useIsSchoolStaff } from "@/components/school/SchoolHub";
import { schoolApi } from "@/lib/school";
import Landing from "@/pages/Landing";
import Schools from "@/pages/Schools";
import SchoolOnboardingForm from "@/pages/SchoolOnboardingForm";
import ExternalLogin from "@/pages/ExternalLogin";
import SsoCallback from "@/pages/SsoCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Today from "@/pages/Today";
import TeacherHome from "@/pages/TeacherHome";
import ClassStories from "@/components/ClassStories";
import ProgressPage from "@/pages/Progress";
import StoryDetail from "@/pages/StoryDetail";
import AdminShell, { RequireAdminOnly } from "@/components/admin/AdminShell";
import AdminStories from "@/pages/AdminStories";
import AdminStoryEditor from "@/pages/AdminStoryEditor";
import AdminInbox from "@/pages/AdminInbox";
import AdminSchools from "@/pages/AdminSchools";
import AdminSchoolAnalytics from "@/pages/AdminSchoolAnalytics";
import AdminUsers from "@/pages/AdminUsers";
import AdminReleaseManager from "@/pages/AdminReleaseManager";
import AdminWeeklyRecaps from "@/pages/AdminWeeklyRecaps";
import AdminLearnerOverrides from "@/pages/AdminLearnerOverrides";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminAuditLog from "@/pages/AdminAuditLog";
import TeacherDashboard from "@/pages/TeacherDashboard";
import FamilyDashboard from "@/pages/FamilyDashboard";
import Community from "@/pages/Community";
import SchoolPage from "@/pages/SchoolPage";
import PrincipalShell from "@/pages/PrincipalShell";
import PrincipalDashboard from "@/pages/PrincipalDashboard";
import PrincipalTeachers from "@/pages/PrincipalTeachers";
import PrincipalStudents from "@/pages/PrincipalStudents";
import PrincipalProgress from "@/pages/PrincipalProgress";
import PrincipalNotices from "@/pages/PrincipalNotices";
import DemoViewSwitcher from "@/components/DemoViewSwitcher";
import PwaNotices from "@/components/PwaNotices";
import "@/App.css";

// A principal's dashboard is a single shortcut into their school (the same "Manage your school" card Little Verses
// shows): they have no stories of their own to follow. A principal whose school is managed in the HikmahVerses
// dashboard (the usual case) goes to the shared school page; the older principal area only serves a principal whose
// school was created inside Stories itself.
function PrincipalHome() {
  const navigate = useNavigate();
  const gate = useIsSchoolStaff(schoolApi, true);
  const target = "/principal";
  return (
    <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="principal-home">
      <section className="hv-card p-6 sm:p-8 flex items-center gap-5 flex-wrap" data-testid="dashboard-role-shortcut">
        <span className="rounded-2xl p-4 shrink-0" style={{ background: "#E6F7EF" }}>
          <School className="w-7 h-7 text-hv-teal" strokeWidth={2.2} />
        </span>
        <div className="flex-1 min-w-[200px]">
          <h1 className="hv-heading text-2xl sm:text-3xl">Manage your school</h1>
          <p className="text-hv-slate mt-1">See every teacher&apos;s classes and students, and add teachers of your own.</p>
        </div>
        <button type="button" onClick={() => navigate(target)} disabled={!gate.checked}
          className="hv-btn-primary inline-flex items-center gap-2 shrink-0 disabled:opacity-60" data-testid="dashboard-role-shortcut-go">
          Go to My School <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
        </button>
      </section>
      <ClassStories heading="Stories" allLocked note="The stories your school's students follow. The first four are open to try; the rest are shown for reference." />
    </main>
  );
}

// A teacher's dashboard is their class: pick the class and section, start it, take attendance, open its stories.
function PrincipalHomeGuard({ children }) {
  const { user } = useAuth();
  if (user && user.role === "principal") return <PrincipalHome />;
  if (user && user.role === "teacher") return <TeacherHome />;
  return children;
}

// The older /principal pages read Stories' own school records. A principal whose school lives in the dashboard has
// none there (it would say "not assigned as principal of a school yet"), so they are sent to the shared school page.
function PrincipalAreaGate({ children }) {
  // The principal's own area (Overview / Teachers / Students / Progress / Notices) is the principal's home again; a
  // school managed in the dashboard is mirrored into it by the backend, so it shows that school's real data.
  return children;
}

function ProtectedRoute({ children, adminOnly = false, principalOnly = false, strictAdmin = false }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-12 text-hv-slate">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && !(user.role === "admin" || user.role === "teacher")) return <Navigate to="/dashboard" replace />;
  if (strictAdmin && user.role !== "admin") return <Navigate to="/dashboard" replace />;   // the admin console is not for teachers
  if (principalOnly && !(user.role === "admin" || user.role === "principal")) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppShell() {
  const location = useLocation();
  // Login/Signup are full-bleed split-panel screens with their own in-panel
  // brand mark (AuthShell) — the site's own header above them would just
  // double up the logo and eat vertical space. /admin is its own self-
  // contained shell (AdminShell has its own sidebar) — stacking the public
  // site nav above it duplicated Today/Progress/Register-school/etc. that
  // have no place in a staff tool, so it's hidden there too.
  // The story player is a full-screen reading experience with its own Back
  // button and title (same as the sibling app's poem player), so the site
  // header is hidden there too.
  const hideNav = location.pathname === "/login" || location.pathname === "/signup"
    || location.pathname.startsWith("/admin") || location.pathname.startsWith("/story/")
    || location.pathname === "/onboarding"
    || location.pathname === "/school";   // the school pages bring their own bar (back to dashboard + tabs), as in Poems
  return (
    <div className="min-h-screen">
      {!hideNav && <DemoViewSwitcher />}
      {!hideNav && <Nav />}
      {/* Mounted on every route, including the story player: a quiz is taken
          there, so the offline sync and the update prompt must run there too. */}
      <PwaNotices />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/families" element={<Landing />} />
        <Route path="/schools" element={<Schools />} />
        {/* Public on purpose: the staff member filling this in has no account yet. */}
        <Route path="/schools/onboarding" element={<SchoolOnboardingForm />} />
        <Route path="/login" element={<ExternalLogin />} />
        <Route path="/signup" element={<ExternalLogin mode="register" />} />
        <Route path="/sso/callback" element={<SsoCallback />} />
        {/* <Route path="/login" element={<Login />} /> */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* <Route path="/signup" element={<Register />} /> */}
        {/* Onboarding is switched off for now (it will live in the Super App login). There is no cohort to choose: each learner starts the Monday after first opening Stories. */}
        <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
        <Route path="/today" element={<Navigate to="/dashboard/today" replace />} />
        <Route path="/dashboard" element={<ProtectedRoute><PrincipalHomeGuard><ProgressPage /></PrincipalHomeGuard></ProtectedRoute>} />
        {/* Public on purpose, same reasoning as /schools/onboarding above:
            the landing page's "Try Story #1 free" button sends a signed-out
            visitor straight here with no auth check at all — gating happens
            inside StoryDetail itself based on auth state (anonymous: video
            only; signed in: full story, even before the learner's start
            date, for story 1 specifically — see FREE_PREVIEW_SEQ on
            the backend). Every other seq still 401s for a signed-out
            visitor, which StoryDetail turns into a redirect to /login. */}
        <Route path="/story/:seq" element={<StoryDetail />} />
        <Route path="/teacher" element={<ProtectedRoute adminOnly><TeacherDashboard /></ProtectedRoute>} />
        <Route path="/teacher/prepare" element={<Navigate to="/dashboard" replace />} />
        <Route path="/principal" element={<ProtectedRoute principalOnly><PrincipalAreaGate><PrincipalShell /></PrincipalAreaGate></ProtectedRoute>}>
          <Route index element={<PrincipalDashboard />} />
          <Route path="teachers" element={<PrincipalTeachers />} />
          <Route path="students" element={<PrincipalStudents />} />
          <Route path="progress" element={<PrincipalProgress />} />
          <Route path="notices" element={<PrincipalNotices />} />
        </Route>
        <Route path="/family" element={<ProtectedRoute><FamilyDashboard /></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/school" element={<ProtectedRoute><SchoolPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly strictAdmin><AdminShell /></ProtectedRoute>}>
          <Route index element={<Navigate to="/admin/stories" replace />} />
          <Route path="stories" element={<AdminStories />} />
          <Route path="stories/:seq" element={<AdminStoryEditor />} />
          <Route path="inbox" element={<AdminInbox />} />
          <Route path="schools" element={<RequireAdminOnly><AdminSchools /></RequireAdminOnly>} />
          <Route path="school-progress" element={<RequireAdminOnly><AdminSchoolAnalytics /></RequireAdminOnly>} />
          <Route path="users" element={<RequireAdminOnly><AdminUsers /></RequireAdminOnly>} />
          <Route path="release" element={<RequireAdminOnly><AdminReleaseManager /></RequireAdminOnly>} />
          <Route path="weekly-recaps" element={<RequireAdminOnly><AdminWeeklyRecaps /></RequireAdminOnly>} />
          <Route path="overrides" element={<RequireAdminOnly><AdminLearnerOverrides /></RequireAdminOnly>} />
          <Route path="analytics" element={<RequireAdminOnly><AdminAnalytics /></RequireAdminOnly>} />
          <Route path="audit-log" element={<RequireAdminOnly><AdminAuditLog /></RequireAdminOnly>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <GatewayDemoSwitcher gatewayUrl={process.env.REACT_APP_GATEWAY_URL} getToken={getToken} setToken={setToken}
          homes={{ default: "/", principal: "/dashboard", teacher: "/dashboard" }} />
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}
